import React from "react";
import useExcelUpload, {
  generateRandomAmount,
  generateRandomDescription,
} from "../../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../utils/UploadExcelEnhanced";
import { getCaSettingByKey } from "../../../../builders/settings/CaSetting";

const getInstructions = (assetLines, caTypeOptions) => [
  ["Col", "Comments"],
  [
    "A",
    assetLines.map((row) => row.assetCategory).length > 0
      ? "Ensure that asset category name matches asset category created in FA summary. Available options are:" +
        assetLines.map((row) => row.assetCategory).join(", ")
      : "No Category available. You should add atleast one category before you continue to upload your excel file.",
  ],
  ["B", "Description (Max 60 characters)."],
  [
    "C",
    "Ensure that Col C is based on the following options: " +
      caTypeOptions.join(", "),
  ],
  ["D", "Year of assessment of purchase. Use numeric year value only."],
  ["E", "False if asset is not fully claimed. True if asset is fully claimed."],
  [
    "F",
    "Number of years brought forward. Leave blank if the asset is fully claimed.",
  ],
  ["G", "Cost. Decimal places will be rounded up to the nearest whole number."],
  ["H", "Tax written down value brought forward."],
  ["I", "Sales proceeds."],
  ["J", "Net book value. Optional. Leave blank if not applicable."],
];

const normalizeText = (value) => (value || "").trim().toLowerCase();

const toNumber = (value) => (value === "" || value == null ? 0 : Number(value));

const toBoolean = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() === "true" : !!value;

const getCaSummary = (caSettings, caName) => {
  if (!caName) return "";
  if (caName === "S16 - (LIA / IBA)") return "";

  return getCaSettingByKey(caSettings, caName)?.caSummary || "";
};

const buildDisposalDetail = (row, caSettings, existingDetail = {}) => {
  const uploadedCaName = row[2] || "";
  const isHirePurchase = !!existingDetail.isHirePurchase;
  const caName = isHirePurchase ? existingDetail.CaName || "" : uploadedCaName;
  const twdvBf = toNumber(row[7]);
  const salesProceeds = toNumber(row[8]);
  const netBookValue = toNumber(row[9]);

  return {
    ...existingDetail,
    description: isHirePurchase
      ? existingDetail.description || row[1] || ""
      : row[1] || "",
    CaName: caName,
    CaSummary: isHirePurchase
      ? existingDetail.CaSummary || getCaSummary(caSettings, caName)
      : getCaSummary(caSettings, caName),
    isHirePurchase,
    yaOfPurchase: isHirePurchase ? existingDetail.yaOfPurchase || "" : row[3],
    isFullyClaimed: toBoolean(row[4]),
    noOfYearsBf: isHirePurchase
      ? existingDetail.noOfYearsBf || 0
      : toNumber(row[5]),
    costInDetails: isHirePurchase
      ? existingDetail.costInDetails || 0
      : Math.round(toNumber(row[6])),
    hpPrincipalPaid: isHirePurchase ? existingDetail.hpPrincipalPaid || 0 : 0,
    twdvBf,
    salesProceeds,
    netBookValue,
    bcBa: existingDetail.bcBa || 0,
    disposalGainLoss: salesProceeds - netBookValue,
  };
};

const DisposalExcelUpload = ({
  assetLines,
  caSettings,
  caTypeOptions,
  yearOfAssessment,
  onExcelAddRows,
  open,
  handleCloseDialog,
}) => {
  const categoryOptions = assetLines.map((row) => row.assetCategory);

  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadTemplate,
    handleFileChange,
    handleResetFile,
    handleUpload,
    getTemplateHeadersString,
  } = useExcelUpload({
    fileName: "fa_disposal_upload_template.xlsx",
    instructions: getInstructions(assetLines, caTypeOptions),
    sampleData: Array.from({ length: 3 }, () => ({
      Category: categoryOptions[0],
      Description: generateRandomDescription(),
      "CA Name": caTypeOptions[0],
      "YA of Purchase": yearOfAssessment,
      "Fully Claimed?": "false",
      "No. of Years B/F": 0,
      Cost: generateRandomAmount(),
      "TWDV B/F": generateRandomAmount(),
      "Sales Proceeds": generateRandomAmount(),
      "Net Book Value": "",
    })),
    validationSchema: [
      { type: "text", required: true, fixedValue: categoryOptions },
      { type: "text", required: true },
      { type: "text", required: true, fixedValue: caTypeOptions },
      { type: "number", required: true },
      { type: "boolean", required: true },
      { type: "number" },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
      { type: "number", round: true },
    ],
    onUpload: (excelData, rowCount) => {
      if (rowCount > 0) {
        const updatedAssetLines = assetLines.map((asset) => {
          const matchingRows = excelData.filter(
            (row) =>
              normalizeText(row[0]) === normalizeText(asset.assetCategory)
          );

          if (matchingRows.length === 0) return asset;

          const existingDetails = asset.disposalDetails || [];

          if (asset.isAssetByAsset) {
            const existingDetail = existingDetails[0] || {};
            const uploadedRow = matchingRows[0];

            return {
              ...asset,
              disposalDetails: [
                buildDisposalDetail(uploadedRow, caSettings, existingDetail),
              ],
            };
          }

          const updatedDetails = [...existingDetails];
          const rowsToAppend = [];

          matchingRows.forEach((row) => {
            const matchingExistingIndex = updatedDetails.findIndex(
              (detail) =>
                detail.isHirePurchase &&
                normalizeText(detail.description) === normalizeText(row[1])
            );

            if (matchingExistingIndex !== -1) {
              updatedDetails[matchingExistingIndex] = buildDisposalDetail(
                row,
                caSettings,
                updatedDetails[matchingExistingIndex]
              );
            } else {
              rowsToAppend.push(buildDisposalDetail(row, caSettings));
            }
          });

          return {
            ...asset,
            disposalDetails: [...updatedDetails, ...rowsToAppend],
          };
        });

        onExcelAddRows(updatedAssetLines);
      }
    },
  });

  const fixedValueGroups = [
    {
      label: "Category",
      values: categoryOptions,
    },
    {
      label: "CA Name",
      values: caTypeOptions,
    },
  ];

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadTemplate}
      onFileChange={handleFileChange}
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateHeadersString={getTemplateHeadersString()}
      templateInstructions={getInstructions(assetLines, caTypeOptions)}
      fixedValueGroups={fixedValueGroups}
    />
  );
};

export default DisposalExcelUpload;
